// Locale-aware formatting.
//
// Every number, date and clock the dentist reads goes through here. Two rules
// this file exists to enforce:
//
//   1. NOTHING hardcodes Persian digits. The doctor client's `toFa()` habit
//      renders «۱۲» inside an otherwise English UI; `formatNumber` follows the
//      active locale instead.
//   2. Every date formatter pins `timeZone: 'Asia/Tehran'`. The instants on the
//      wire are UTC epochs and the clinic's day is an Iran day; formatting one
//      in the browser's zone puts a 23:30 appointment on the wrong date for a
//      dentist travelling, and on the right one at home — the worst kind of bug.
//
// Jalali comes from the locale tag (`-u-ca-persian`), never from an `if`: one
// code path renders both calendars, so they cannot drift.

// The ISO-day shape is imported rather than re-declared: lib/iranTime.js owns
// what "a calendar day on the wire" looks like, and this module has to agree
// with it about which strings get the noon-UTC anchor below.
import { ISO_DATE_RE } from '../lib/iranTime.js';

const INTL_TAG = {
  // arabext = ۰۱۲۳۴۵۶۷۸۹, the digits Persian users read.
  fa: 'fa-IR-u-ca-persian-nu-arabext',
  // en-GB rather than en-US: the day-month order and the 24-hour clock match
  // what the Persian side shows, so switching language changes the words and
  // not the shape.
  en: 'en-GB',
  // French mirrors English in this panel: Gregorian, Latin digits, 24-hour.
  // Without it fr would silently take en-GB, and English month and weekday
  // names would sit inside a French UI. Groups with U+202F, decimal comma.
  fr: 'fr-FR',
  // German likewise mirrors English: Gregorian, Latin digits, 24-hour. Groups
  // thousands with '.', decimal comma.
  de: 'de-DE',
};

const TZ = 'Asia/Tehran';

const DATE_STYLES = {
  // «شنبه، ۲۶ تیر» / «Sat, 17 Jul»
  weekdayDayMonth: { weekday: 'short', day: 'numeric', month: 'long' },
  // «شنبه، ۲۶ تیر ۱۴۰۴» / «Saturday, 17 July 2025»
  full: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  // «۲۶ تیر» / «17 July»
  dayMonth: { day: 'numeric', month: 'long' },
  // «۱۴۰۴/۰۴/۲۶» / «17/07/2025»
  numeric: { day: '2-digit', month: '2-digit', year: 'numeric' },
};

/**
 * Accepts an epoch (number or the numeric strings the wire carries) or a plain
 * `YYYY-MM-DD`. A bare date is anchored at noon UTC so no timezone shift can
 * move it onto the neighbouring day.
 */
function toDate(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) {
    const ms = Date.parse(`${value}T12:00:00Z`);
    return Number.isNaN(ms) ? null : new Date(ms);
  }
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return new Date(n);
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : new Date(parsed);
}

export function createFormatters(locale) {
  const tag = INTL_TAG[locale] || INTL_TAG.en;

  const number = new Intl.NumberFormat(tag);
  const time = new Intl.DateTimeFormat(tag, {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ,
  });
  const dates = Object.fromEntries(
    Object.entries(DATE_STYLES).map(([name, opts]) => [
      name, new Intl.DateTimeFormat(tag, { ...opts, timeZone: TZ }),
    ]),
  );

  /** A number in the active locale's digits and grouping. */
  const formatNumber = (n) => (
    Number.isFinite(Number(n)) ? number.format(Number(n)) : ''
  );

  /**
   * Localise the digits inside a string that is NOT a number — a clock («۰۹:۳۰»),
   * a file number, a national id. Grouping separators would corrupt those, which
   * is why this is a different function from formatNumber.
   */
  const formatDigits = (s) => {
    if (s == null) return '';
    const zero = locale === 'fa' ? 0x06F0 : 0x0030;
    return String(s).replace(/\d/g, (d) => String.fromCharCode(zero + Number(d)));
  };

  const formatDate = (value, style = 'weekdayDayMonth') => {
    const d = toDate(value);
    if (!d) return '';
    return (dates[style] || dates.weekdayDayMonth).format(d);
  };

  /** 24-hour, in Iran time, in both languages. A clinic runs on one clock. */
  const formatTime = (value) => {
    const d = toDate(value);
    return d ? time.format(d) : '';
  };

  return { formatNumber, formatDigits, formatDate, formatTime };
}

// The separator between items of an inline list, in the page's script: «، » in
// Persian and Arabic, ", " in English. Read from <html dir> (set by the
// I18nProvider) so plain helpers and tooltips can use it without the hook.
// i18n-exempt: punctuation, not copy.
export function listSeparator() {
  return typeof document !== 'undefined' && document.documentElement.getAttribute('dir') === 'ltr' ? ', ' : '، ';
}

// The placeholder mark in an avatar with no name — «؟» or "?" by the same rule.
// i18n-exempt: a glyph, not copy.
export function unknownMark() {
  return typeof document !== 'undefined' && document.documentElement.getAttribute('dir') === 'ltr' ? '?' : '؟';
}
